import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { errorLogService } from '../services/errorLogService';

/**
 * Hook genérico para operações CRUD usando React Query
 *
 * ATENÇÃO: expõe `create`, `update`, `deleteItem` que apontam para `mutation.mutate`
 * (fire-and-forget). Use sempre `createAsync`, `updateAsync`, `deleteAsync` em handlers
 * com try/await/catch para que erros do backend sejam capturados corretamente.
 * Os callbacks onError abaixo são uma rede de segurança para os casos em que alguém
 * ainda use as variantes síncronas acidentalmente.
 *
 * @param {string} queryKey - Chave única para o cache (ex: 'services', 'places')
 * @param {Object} service - Objeto com métodos getAll, create, update, delete
 * @param {Object} options - Opções adicionais
 * @returns {Object} - { items, loading, error, reload, create, update, deleteItem }
 *
 * @example
 * const { items: services, loading, createAsync, updateAsync, deleteItem } = useQueryCRUD(
 *   'services',
 *   servicesService
 * );
 */
export function useQueryCRUD(queryKey, service, options = {}) {
  const queryClient = useQueryClient();

  // Query para listar items
  const query = useQuery({
    queryKey: [queryKey],
    queryFn: async () => {
      const data = await service.getAll();
      return data || [];
    },
    staleTime: options.staleTime || 5 * 60 * 1000, // 5 minutos
    ...options.queryOptions,
  });

  // Mutation para criar
  const createMutation = useMutation({
    mutationFn: (data) => service.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
    onError: (err) => {
      console.error(`[useQueryCRUD:${queryKey}] create failed`, err);
      errorLogService.logError(err, null, {
        source: 'useQueryCRUD',
        operation: `${queryKey}.create`,
        status: err.response?.status,
      });
    },
    ...options.createOptions,
  });

  // Mutation para atualizar
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => service.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
    onError: (err) => {
      console.error(`[useQueryCRUD:${queryKey}] update failed`, err);
      errorLogService.logError(err, null, {
        source: 'useQueryCRUD',
        operation: `${queryKey}.update`,
        status: err.response?.status,
      });
    },
    ...options.updateOptions,
  });

  // Mutation para deletar
  const deleteMutation = useMutation({
    mutationFn: (id) => service.delete(id),
    onSuccess: (_, id) => {
      // Remove imediatamente do cache para evitar race condition com staleTime:
      // se alguma página reativa aos items fizer chamadas secundárias por item,
      // o item deletado já estará fora da lista antes do refetch completar.
      queryClient.setQueryData([queryKey], (old) => {
        if (!old || !Array.isArray(old)) return old;
        return old.filter((item) => item.id !== id);
      });
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
    onError: (err) => {
      console.error(`[useQueryCRUD:${queryKey}] delete failed`, err);
      errorLogService.logError(err, null, {
        source: 'useQueryCRUD',
        operation: `${queryKey}.delete`,
        status: err.response?.status,
      });
    },
    ...options.deleteOptions,
  });

  return {
    // Data
    items: query.data || [],
    
    // Loading states
    loading: query.isLoading,
    isFetching: query.isFetching,
    
    // Error
    error: query.error,
    
    // Actions
    reload: () => queryClient.invalidateQueries({ queryKey: [queryKey] }),
    
    // CRUD operations
    create: createMutation.mutate,
    createAsync: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    
    // Update aceita (id, data) OU ({ id, data })
    update: (idOrObj, data) => {
      if (typeof idOrObj === 'object' && idOrObj.id) {
        return updateMutation.mutate(idOrObj);
      }
      return updateMutation.mutate({ id: idOrObj, data });
    },
    updateAsync: (idOrObj, data) => {
      if (typeof idOrObj === 'object' && idOrObj.id) {
        return updateMutation.mutateAsync(idOrObj);
      }
      return updateMutation.mutateAsync({ id: idOrObj, data });
    },
    isUpdating: updateMutation.isPending,
    
    deleteItem: deleteMutation.mutate,
    deleteAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}

export default useQueryCRUD;
